import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const DAYS = [
  { key: 'monday', label: 'Lun', fullLabel: 'Lunes' },
  { key: 'tuesday', label: 'Mar', fullLabel: 'Martes' },
  { key: 'wednesday', label: 'Mie', fullLabel: 'Miercoles' },
  { key: 'thursday', label: 'Jue', fullLabel: 'Jueves' },
  { key: 'friday', label: 'Vie', fullLabel: 'Viernes' },
  { key: 'saturday', label: 'Sab', fullLabel: 'Sabado' },
  { key: 'sunday', label: 'Dom', fullLabel: 'Domingo' },
];

/**
 * Convierte end_hour del backend (entero exclusivo)
 * al string HH:MM para input type="time".
 * Caso especial: 24 → "23:59" (todo el día)
 * Resto: N → "NN:00"
 */
const endHourToTime = (hour) => {
  if (hour == null) return '17:00';
  if (hour === 24) return '23:59';
  return `${String(hour).padStart(2, '0')}:00`;
};

/**
 * Convierte string HH:MM del input type="time"
 * a end_hour entero exclusivo para el backend.
 * Caso especial: "23:59" → 24 (todo el día)
 * Resto: "HH:MM" → parseInt(HH)
 */
const timeToEndHour = (time) => {
  if (!time) return 24;
  if (time === '23:59') return 24;
  return parseInt(time.split(':')[0], 10);
};

/**
 * Convierte start_hour del backend a string HH:MM.
 * Sin caso especial — siempre horas enteras.
 */
const startHourToTime = (hour) => {
  if (hour == null) return '09:00';
  return `${String(hour).padStart(2, '0')}:00`;
};

/**
 * Convierte string HH:MM a start_hour entero.
 */
const timeToStartHour = (time) => {
  if (!time) return 0;
  return parseInt(time.split(':')[0], 10);
};

// Convierte schedule backend ({day:{active,start_hour,end_hour}}) a form interno
// ({day:{active,start:'HH:MM',end:'HH:MM'}}).
const toForm = (schedule) => {
  const safe = (schedule && typeof schedule === 'object') ? schedule : {};
  const out = {};
  DAYS.forEach(d => {
    const ex = safe[d.key] || {};
    const defaultActive = d.key !== 'saturday' && d.key !== 'sunday';
    out[d.key] = {
      active: ex.active !== undefined ? !!ex.active : defaultActive,
      start: startHourToTime(ex.start_hour ?? 9),
      end: endHourToTime(ex.end_hour ?? 17),
    };
  });
  return out;
};

// Convierte form interno a formato backend.
const fromForm = (form) => {
  const out = {};
  DAYS.forEach(d => {
    const v = form[d.key] || {};
    out[d.key] = {
      active: !!v.active,
      start_hour: timeToStartHour(v.start),
      end_hour: timeToEndHour(v.end),
    };
  });
  return out;
};

/**
 * Modal de horario por vendedor.
 *
 * Props:
 *  - open: bool
 *  - onOpenChange: (open: bool) => void
 *  - vendorName: string
 *  - schedule: { [day]: { active, start_hour, end_hour } }
 *  - onSave: (schedule) => void | Promise — recibe schedule en formato backend
 *  - saving?: bool — deshabilita el botón guardar mientras se persiste
 */
export default function VendorScheduleModal({
  open,
  onOpenChange,
  vendorName,
  schedule,
  onSave,
  saving = false,
}) {
  const [form, setForm] = useState(() => toForm(schedule));
  const [selectedDay, setSelectedDay] = useState('monday');

  // Reset form when opening with a different schedule
  useEffect(() => {
    if (open) {
      const initialForm = toForm(schedule);
      setForm(initialForm);
      const firstActive = DAYS.find(d => initialForm[d.key]?.active) || DAYS[0];
      setSelectedDay(firstActive.key);
    }
  }, [open, schedule]);

  const day = form[selectedDay] || {};
  const dayObj = DAYS.find(d => d.key === selectedDay) || DAYS[0];

  // "Todos los días" reflejado a partir de form (no es un state independiente).
  const allDaysActive = DAYS.every(d => !!form[d.key]?.active);

  const toggleAllDays = (next) => {
    const upd = { ...form };
    DAYS.forEach(d => {
      const cur = upd[d.key] || {};
      upd[d.key] = {
        ...cur,
        active: next,
        start: cur.start || '09:00',
        end: cur.end || '17:00',
      };
    });
    setForm(upd);
  };

  const toggleActive = () => {
    setForm({
      ...form,
      [selectedDay]: {
        ...day,
        active: !day.active,
        start: day.start || '09:00',
        end: day.end || '17:00',
      },
    });
  };

  const setAllDay = () => {
    const upd = { ...form };
    DAYS.forEach(dd => {
      if (upd[dd.key]?.active) {
        upd[dd.key] = {
          ...upd[dd.key],
          start: startHourToTime(0),   // "00:00"
          end: endHourToTime(24),      // "23:59"
        };
      }
    });
    setForm(upd);
  };

  // Cambiar Desde/Hasta SIEMPRE se aplica a todos los días con active=true.
  // Los días inactivos no se tocan.
  const setStart = (val) => {
    const upd = { ...form };
    DAYS.forEach(dd => {
      if (upd[dd.key]?.active) upd[dd.key] = { ...upd[dd.key], start: val };
    });
    setForm(upd);
  };

  const setEnd = (val) => {
    const upd = { ...form };
    DAYS.forEach(dd => {
      if (upd[dd.key]?.active) upd[dd.key] = { ...upd[dd.key], end: val };
    });
    setForm(upd);
  };

  const handleSave = () => {
    if (typeof onSave === 'function') onSave(fromForm(form));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-lg" data-testid="vendor-schedule-modal">
        <DialogHeader>
          <DialogTitle className="text-sm">Horarios — {vendorName || ''}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-3" style={{ minHeight: '300px' }}>
          {/* Columna izquierda: dias */}
          <div className="w-[140px] shrink-0 space-y-2 border-r border-white/5 pr-3">
            <label className="flex items-center justify-between gap-2 px-1 pb-2 border-b border-white/5 cursor-pointer" data-testid="vendor-schedule-same-toggle">
              <span className="text-[10px] text-muted-foreground">Todos los dias</span>
              <Switch checked={allDaysActive} onCheckedChange={toggleAllDays} />
            </label>
            <div className="space-y-1">
              {DAYS.map(d => {
                const dval = form[d.key] || {};
                const isSelected = selectedDay === d.key;
                const isActive = !!dval.active;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setSelectedDay(d.key)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-[11px] transition-all ${
                      isSelected
                        ? 'bg-violet-500/15 border border-violet-500/30 text-white font-medium'
                        : 'border border-transparent text-muted-foreground hover:bg-white/[0.03]'
                    }`}
                    data-testid={`vendor-schedule-day-${d.key}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-emerald-400' : 'bg-white/20'}`} />
                    <span className={isActive ? 'text-white' : ''}>{d.fullLabel || d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Columna derecha: configuracion */}
          <div className="flex-1 space-y-3 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs text-white font-medium">Configuracion — {dayObj.fullLabel || dayObj.label}</p>
            </div>
            <label className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white/[0.03] border border-white/5 cursor-pointer" data-testid="vendor-schedule-active-toggle">
              <span className="text-[11px] text-white">Activo este dia</span>
              <Switch checked={!!day.active} onCheckedChange={toggleActive} />
            </label>
            {day.active ? (
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1.5">Opcion rapida</p>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-[10px] border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10" onClick={setAllDay} data-testid="vendor-schedule-all-day">
                    Todo el dia (00:00 – 23:59)
                  </Button>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1.5">O configurar horario especifico</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-[9px] text-muted-foreground mb-0.5">Desde</p>
                      <Input type="time" value={day.start || '09:00'} onChange={e => setStart(e.target.value)} className="bg-black/20 border-white/10 text-[11px] h-8" data-testid="vendor-schedule-start" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[9px] text-muted-foreground mb-0.5">Hasta</p>
                      <Input type="time" value={day.end || '17:00'} onChange={e => setEnd(e.target.value)} className="bg-black/20 border-white/10 text-[11px] h-8" data-testid="vendor-schedule-end" />
                    </div>
                  </div>
                </div>
                <p className="text-[9px] text-amber-400/70 px-2 py-1.5 rounded bg-amber-500/5 border border-amber-500/10">Los cambios de horario se aplican a todos los dias activos.</p>
              </div>
            ) : (
              <div className="px-3 py-6 rounded-md bg-white/[0.02] border border-white/5 text-center">
                <p className="text-[10px] text-muted-foreground italic">Este vendedor no recibe leads este dia.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-white/5">
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => onOpenChange(false)} data-testid="vendor-schedule-cancel">Cancelar</Button>
          <Button size="sm" className="text-xs" onClick={handleSave} disabled={saving} data-testid="vendor-schedule-save">
            {saving ? 'Guardando...' : 'Guardar horarios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
